import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Loader2, Save, Plus, Trash2 } from "lucide-react";
import { zoomCard } from "@/components/CardZoom";

export default function PrizeBoxPanel() {
  const [sets, setSets] = useState(null);
  const [metrics, setMetrics] = useState({});
  const [cards, setCards] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/vault/sets").then(({ data }) => { setSets(data.sets); setMetrics(data.metrics); }).catch(() => {});
    api.get("/admin/vault/cards").then(({ data }) => setCards(data)).catch(() => {});
  }, []);

  const upd = (i, k, v) => setSets((l) => l.map((s, j) => (j === i ? { ...s, [k]: v } : s)));

  const toggleCard = (i, cid) => setSets((l) => l.map((s, j) => {
    if (j !== i) return s;
    const ids = s.card_ids || [];
    return { ...s, card_ids: ids.includes(cid) ? ids.filter((x) => x !== cid) : [...ids, cid] };
  }));

  const add = () => setSets((l) => [...l, { id: null, name: "", desc: "", metric: "unique_cards", need: 5, reward: 2, badge: "", card_ids: [] }]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/vault/sets", {
        sets: sets.map((s) => ({ ...s, need: Number(s.need) || 1, reward: Number(s.reward) || 0 })),
      });
      setSets(data.sets);
      toast.success("Prize boxes updated — live in every player's Vault");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!sets) return null;
  return (
    <div className="border border-pb-border bg-pb-surface p-5" data-testid="prize-box-panel">
      <div className="flex items-center gap-2 mb-1"><Gift className="w-4 h-4 text-pb-cyan" /><span className="font-display font-bold uppercase">Prize Boxes (Collection Sets)</span></div>
      <p className="text-xs text-zinc-500 mb-4">
        The one-time bonus-coin rewards players chase (Gold Rush, Holo Hunter…). Edit how many they need, the BLC reward
        (paid from the Red Vault), rename them, or add new ones. Rewards a player has already claimed stay claimed.
      </p>
      <div className="space-y-3">
        {sets.map((s, i) => (
          <div key={s.id || `new-${i}`} className="flex flex-wrap items-end gap-3 border border-pb-border/60 bg-pb-elevated/40 p-3" data-testid={`prize-box-row-${i}`}>
            <div className="w-36">
              <label className="block text-[8px] uppercase tracking-widest text-zinc-600 mb-1">Name</label>
              <input data-testid={`prize-name-${i}`} value={s.name} onChange={(e) => upd(i, "name", e.target.value)} maxLength={40}
                className="w-full bg-pb-surface border border-pb-border px-2.5 py-2 text-xs text-white focus:outline-none focus:border-pb-red" />
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="block text-[8px] uppercase tracking-widest text-zinc-600 mb-1">Description shown to players</label>
              <input data-testid={`prize-desc-${i}`} value={s.desc} onChange={(e) => upd(i, "desc", e.target.value)} maxLength={120}
                className="w-full bg-pb-surface border border-pb-border px-2.5 py-2 text-xs text-white focus:outline-none focus:border-pb-red" />
            </div>
            <div className="w-52">
              <label className="block text-[8px] uppercase tracking-widest text-zinc-600 mb-1">Goal type</label>
              <select data-testid={`prize-metric-${i}`} value={s.metric} onChange={(e) => upd(i, "metric", e.target.value)}
                className="w-full bg-pb-surface border border-pb-border px-2 py-2 text-xs text-white focus:outline-none focus:border-pb-red">
                {Object.entries(metrics).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
              </select>
            </div>
            <div className="w-20">
              <label className="block text-[8px] uppercase tracking-widest text-zinc-600 mb-1">Needed</label>
              <input data-testid={`prize-need-${i}`} type="number" min={1} disabled={s.metric === "card_list"}
                value={s.metric === "card_list" ? (s.card_ids || []).length : s.need} onChange={(e) => upd(i, "need", e.target.value)}
                className="w-full bg-pb-surface border border-pb-border px-2.5 py-2 text-xs text-white font-mono focus:outline-none focus:border-pb-red disabled:opacity-50" />
            </div>
            <div className="w-24">
              <label className="block text-[8px] uppercase tracking-widest text-zinc-600 mb-1">Reward BLC</label>
              <input data-testid={`prize-reward-${i}`} type="number" min={0} step="0.5" value={s.reward} onChange={(e) => upd(i, "reward", e.target.value)}
                className="w-full bg-pb-surface border border-pb-border px-2.5 py-2 text-xs text-white font-mono focus:outline-none focus:border-pb-red" />
            </div>
            <div className="w-24">
              <label className="block text-[8px] uppercase tracking-widest text-zinc-600 mb-1">Badge</label>
              <input data-testid={`prize-badge-${i}`} value={s.badge || ""} onChange={(e) => upd(i, "badge", e.target.value)} maxLength={30}
                placeholder="🏆 Series 1" title="Badge collectors earn when they complete this set"
                className="w-full bg-pb-surface border border-pb-border px-2.5 py-2 text-xs text-white focus:outline-none focus:border-pb-red" />
            </div>
            <button data-testid={`prize-delete-${i}`} onClick={() => setSets((l) => l.filter((_, j) => j !== i))} title="Remove this prize box"
              className="p-2 border border-pb-border text-zinc-500 hover:border-red-500 hover:text-red-400 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
            {s.metric === "card_list" && (
              <div className="w-full" data-testid={`prize-cardpicker-${i}`}>
                <div className="text-[8px] uppercase tracking-widest text-zinc-600 mb-1.5">
                  Set Builder — tap the cards this set requires ({(s.card_ids || []).length} picked)
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {cards.map((c) => {
                    const on = (s.card_ids || []).includes(c.id);
                    return (
                      <button key={c.id} data-testid={`prize-card-${i}-${c.id}`} onClick={() => toggleCard(i, c.id)}
                        title={c.subject_name}
                        className={`shrink-0 w-14 border-2 transition-colors ${on ? "border-pb-cyan" : "border-pb-border opacity-50 hover:opacity-90"}`}>
                        <img src={c.url} alt={c.subject_name} className="w-full aspect-[3/4] object-contain bg-black cursor-zoom-in"
                          title="Tap to zoom" onClick={() => zoomCard(c.url, c.subject_name)} />
                        <div className="text-[7px] truncate px-0.5 text-zinc-400">{c.subject_name}</div>
                      </button>
                    );
                  })}
                  {cards.length === 0 && <span className="text-[10px] text-zinc-600">No cards minted yet.</span>}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-4">
        <button data-testid="prize-box-add" onClick={add}
          className="flex items-center gap-1.5 border border-pb-cyan text-pb-cyan font-black uppercase tracking-widest px-4 py-2.5 text-[10px] hover:bg-pb-cyan hover:text-black transition-colors">
          <Plus className="w-3.5 h-3.5" /> Add Prize Box
        </button>
        <button data-testid="prize-box-save" onClick={save} disabled={busy}
          className="flex items-center gap-1.5 bg-pb-red text-black font-bold uppercase tracking-widest px-6 py-2.5 text-xs hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Prize Boxes
        </button>
      </div>
    </div>
  );
}
